<template>
  <div class="page-with-subnav">
    <app-sub-navigation-bar
      back-label="Back to all products"
      @back="$router.push('/settings/product')"
    />
    <div class="main-container page-with-subnav__content">
      <div class="ui grid product-form-grid">
        <div class="sixteen wide column">
          <h1 class="product-form-heading">
            <i class="edit outline icon"></i>
            {{ pageTitle }}
          </h1>
        </div>
        <div class="sixteen wide column">
          <app-form-error></app-form-error>
          <form class="ui form product-form-inner" @submit.prevent="saveProduct">
            <h4 class="ui dividing header">Product Images</h4>

            <app-images
              :parent_item="material"
              :save="saveProduct"
              @images="onImagesChange"
              :can-upload="!disabled"
            />

            <h4 class="ui dividing header">Product Details</h4>

            <div class="field settings">
              <label>Description*</label>
              <app-text-area
                rows="3"
                placeholder="Enter product description. (This will appear on quote/invoice)"
                :model-value="material.description"
                :disabled="disabled"
                :readonly="disabled"
                :error="validation('description')"
                @update:model-value="material.description = $event"
              />
            </div>

            <div class="three fields">
              <div class="field settings">
                <label>Code</label>
                <app-text-input
                  placeholder="Auto generated"
                  :model-value="material.code"
                  disabled
                />
              </div>
              <div class="field settings">
                <label>Brand*</label>
                <app-select
                  :defaultLabel="'Enter brand'"
                  :value="material.brand"
                  :disabled="disabled"
                  :readonly="disabled"
                  :items="$store.getters['product/getBrands']"
                  @selected="material.brand = $event.id"
                />
              </div>
              <div class="field settings">
                <label>Type*</label>
                <app-select
                  v-bind:class="{ error: validation('type') }"
                  :defaultLabel="'Select type'"
                  :disabled="disabled"
                  :readonly="disabled"
                  :items="$store.getters['material/getTypes']"
                  :value="material.type"
                  @selected="onTypeSelected"
                />
              </div>
            </div>

            <h4 class="ui dividing header">Supplier Details</h4>

            <div class="two fields">
              <div class="field settings">
                <label>Main supplier*</label>
                <app-select
                  v-bind:class="{ error: validation('primary_supplier') }"
                  :defaultLabel="'Select supplier'"
                  :value="material.primary_supplier"
                  :disabled="disabled"
                  :readonly="disabled"
                  :items="$store.getters['product/getSuppliers']"
                  @selected="material.primary_supplier = $event.id"
                />
              </div>
              <div class="field settings">
                <label>Supplier ID</label>
                <app-text-input
                  placeholder="Enter supplier ID"
                  :model-value="material.supplier_id"
                  :disabled="disabled"
                  :readonly="disabled"
                  @update:model-value="material.supplier_id = $event"
                />
              </div>
            </div>

            <div class="three fields">
              <div class="field settings">
                <label>Size (diameter)</label>
                <app-select
                  :defaultLabel="'n/a'"
                  :disabled="disabled"
                  :readonly="disabled"
                  :items="$store.getters['material/getSize']"
                  :value="material.size"
                  @selected="material.size = $event.id"
                />
              </div>
              <div class="field settings">
                <label>Warranty</label>
                <app-text-input
                  placeholder="e.g. 10 years parts, 25 years heat exchanger"
                  :model-value="material.warranty"
                  :disabled="disabled"
                  :readonly="disabled"
                  @update:model-value="material.warranty = $event"
                />
              </div>
              <div class="field settings">
                <label>Stocked item?</label>
                <app-select
                  :items="[{ id: 0, name: 'No' }, { id: 1, name: 'Yes' }]"
                  :value="material.stocked"
                  :disabled="disabled"
                  :readonly="disabled"
                  @selected="material.stocked = $event.id"
                />
              </div>
            </div>

            <h4 class="ui dividing header">Pricing Details</h4>

            <div class="three fields">
              <div class="field settings">
                <label>Price Method</label>
                <app-select
                  :defaultLabel="'Select price method'"
                  :items="priceMethodOptions"
                  :value="material.price_method"
                  :disabled="disabled"
                  :readonly="disabled"
                  @selected="onPriceMethodChange($event.id)"
                />
              </div>
              <div class="field settings" v-if="material.price_method !== 'standard_price'">
                <label>Markup %</label>
                <div class="ui right labeled input" v-if="material.price_method === 'standard_markup'">
                  <input type="text" :value="standardMarginPctDisplay" disabled>
                  <div class="ui label">%</div>
                </div>
                <div class="ui right labeled input" v-else>
                  <input
                    class="product-amount-input"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    v-model="material.markup_pct"
                    :disabled="disabled"
                    @input="recalculateSellPrice"
                  >
                  <div class="ui label">%</div>
                </div>
              </div>
              <div class="field settings" v-else></div>
              <div class="field settings">
                <label>Includes GST*</label>
                <app-select
                  :items="includesGstOptions"
                  :value="material.includes_gst"
                  :disabled="disabled"
                  :readonly="disabled"
                  @selected="material.includes_gst = $event.id"
                />
              </div>
            </div>

            <div class="two fields">
              <div class="field settings">
                <label>Cost Price</label>
                <div class="ui left labeled input">
                  <div class="ui label">$</div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    v-model="material.cost"
                    :disabled="disabled"
                    v-bind:class="{ error: validation('cost') }"
                    @input="recalculateSellPrice"
                  >
                </div>
              </div>
              <div class="field settings">
                <label>Sell Price*</label>
                <div class="ui left labeled input">
                  <div class="ui label">$</div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    v-model="material.sell_price"
                    :disabled="disabled || !isStandardPrice"
                    :readonly="!isStandardPrice"
                    v-bind:class="{ error: validation('sell_price') }"
                  >
                </div>
              </div>
            </div>

            <div class="field product-form-actions">
              <button type="submit" class="ui primary button" v-if="canSave">Save Changes</button>
              <button type="button" class="ui negative basic button" v-if="canDeleteMaterial" @click="deleteProduct">Trash</button>
              <button
                type="button"
                class="ui negative basic button"
                v-if="material.id && material.default && !$store.getters['user/isPlatformAdmin']"
                @click="resetProduct"
              >
                Reset to default
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import auth from '../../helpers/auth'
import SubNavigationBar from '../../components/common/SubNavigationBar.vue'
import {
  PRICE_METHOD_OPTIONS,
  INCLUDES_GST_OPTIONS,
  PRICE_METHODS,
  resolveSellPrice,
  includesGstFlag,
  DEFAULT_STANDARD_MARGIN_PCT
} from '../../helpers/productPricing'
import { familyToParentType, PARENT_TYPE } from '../../helpers/materialFamily'

const emptyMaterial = () => ({
  description: '',
  cost: '',
  sell_price: '',
  price_method: PRICE_METHODS.STANDARD_MARKUP,
  markup_pct: '',
  includes_gst: 1,
  is_active: true,
  primary_supplier: null,
  supplier_id: '',
  size: null,
  brand: null,
  type: null,
  material_type_id: null,
  family: null,
  task_group: PARENT_TYPE.PRODUCT,
  classification_override: 0,
  classification_source: null,
  stocked: 1,
  warranty: '',
  code: '',
  images: []
})

function parseImages (value) {
  if (value == null || value === '') return []
  if (Array.isArray(value)) return value
  if (typeof value === 'object') return [value]
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : (parsed ? [parsed] : [])
  } catch (e) {
    return []
  }
}

export default {
  name: 'ProductForm',
  components: {
    'app-sub-navigation-bar': SubNavigationBar
  },
  data () {
    return {
      material: emptyMaterial(),
      standardMarginPct: DEFAULT_STANDARD_MARGIN_PCT,
      priceMethodOptions: PRICE_METHOD_OPTIONS,
      includesGstOptions: INCLUDES_GST_OPTIONS
    }
  },
  computed: {
    isNew () {
      return this.$route.name === 'ProductNew'
    },
    pageTitle () {
      return this.isNew ? 'New Product' : 'Manage Product'
    },
    isStandardPrice () {
      return this.material.price_method === PRICE_METHODS.STANDARD_PRICE
    },
    standardMarginPctDisplay () {
      return parseFloat(this.standardMarginPct).toFixed(2)
    },
    disabled () {
      if (this.$store.getters['user/isPlatformAdmin']) return false
      if (!this.material || !this.material.id) return false
      const materialCompanyId = this.material.company_id
      return materialCompanyId === null || materialCompanyId === undefined
    },
    canSave () {
      if (this.$store.getters['user/isPlatformAdmin']) return true
      if (!this.material || !this.material.id) return true
      const materialCompanyId = this.material.company_id
      return materialCompanyId !== null && materialCompanyId !== undefined
    },
    canDeleteMaterial () {
      if (!this.material || !this.material.id) return false
      if (this.$store.getters['user/isPlatformAdmin']) return true
      const userCompanyId = this.$store.getters['user/getProfile'] && this.$store.getters['user/getProfile'].company_id
      const materialCompanyId = this.material.company_id
      return materialCompanyId != null && materialCompanyId === userCompanyId
    }
  },
  watch: {
    '$route' () {
      this.bootstrapFromRoute()
    }
  },
  created () {
    auth.check(this)
    if (!this.$store.state.product.suppliers || !this.$store.state.product.suppliers.length) {
      this.$store.dispatch('product/fetchSuppliers')
    }
    this.bootstrapFromRoute()
  },
  methods: {
    loadAccountMargin () {
      return axios.get('/api/setting/settings').then((res) => {
        const pct = res.data && res.data.standard_margin_pct
        if (pct != null && pct !== '') {
          this.standardMarginPct = parseFloat(pct) || DEFAULT_STANDARD_MARGIN_PCT
        }
      }).catch(() => {})
    },
    bootstrapFromRoute () {
      if (this.isNew) {
        this.material = emptyMaterial()
        this.loadAccountMargin().then(() => this.recalculateSellPrice())
        return
      }
      const id = this.$route.params.id
      if (!id) return
      Promise.all([
        this.loadAccountMargin(),
        axios.get('/api/materials/' + id)
      ]).then(([, res]) => {
        const m = res.data && res.data.material
        if (!m) {
          this.errorNotification('Product not found')
          this.$router.replace('/settings/product')
          return
        }
        this.material = Object.assign(emptyMaterial(), m, {
          includes_gst: includesGstFlag(m.includes_gst) ? 1 : 0,
          price_method: m.price_method || PRICE_METHODS.STANDARD_MARKUP,
          markup_pct: m.markup_pct != null ? String(m.markup_pct) : '',
          cost: m.cost != null ? String(m.cost) : '',
          sell_price: m.sell_price != null ? String(m.sell_price) : '',
          images: parseImages(m.images),
          material_type_id: m.material_type_id || null,
          family: m.family || null,
          classification_override: m.classification_override ? 1 : 0,
          classification_source: m.classification_source || null
        })
        this.syncTaskGroupFromType()
        this.$set(this.material, 'images', parseImages(this.material.images))
        this.mapSizeDisplay(m)
        this.initializeSupplier()
        if (!this.isStandardPrice) {
          this.recalculateSellPrice()
        }
      }).catch(() => {
        this.errorNotification('Failed to load product')
        this.$router.replace('/settings/product')
      })
    },
    mapSizeDisplay (m) {
      if (!m.size_display) return
      const sizes = this.$store.getters['material/getSize']
      const size = sizes.find((s) => s.name === m.size_display)
      if (size) this.material.size = size.id
    },
    initializeSupplier () {
      if (this.material.supplier_name && !this.material.primary_supplier) {
        if (this.material.supplier_name === 'Reece') this.material.primary_supplier = 1
        else if (this.material.supplier_name === 'Bunnings') this.material.primary_supplier = 2
        else if (this.material.supplier_name === 'Mitre 10') this.material.primary_supplier = 3
        else this.material.primary_supplier = 4
      }
    },
    onImagesChange (images) {
      this.$set(this.material, 'images', Array.isArray(images) ? images : [])
    },
    onPriceMethodChange (method) {
      this.material.price_method = method
      this.recalculateSellPrice()
    },
    familyForLegacyType (legacyTypeId) {
      const id = Number(legacyTypeId)
      if (!Number.isFinite(id)) return null
      const pipes = this.$store.getters['material/getTypePipe'] || []
      const fittings = this.$store.getters['material/getTypeFitting'] || []
      const fixings = this.$store.getters['material/getTypeFixing'] || []
      if (fittings.some(t => Number(t.id) === id)) return 'fitting'
      if (fixings.some(t => Number(t.id) === id)) return 'fixing'
      if (pipes.some(t => Number(t.id) === id)) return 'pipe'
      return 'product'
    },
    syncTaskGroupFromType () {
      if (this.material.family) {
        this.material.task_group = familyToParentType(this.material.family)
        return
      }
      const family = this.familyForLegacyType(this.material.type)
      this.material.family = family
      this.material.task_group = familyToParentType(family)
    },
    onTypeSelected (event) {
      this.material.type = event.id
      this.material.family = this.familyForLegacyType(event.id)
      this.material.task_group = familyToParentType(this.material.family)
    },
    recalculateSellPrice () {
      if (this.isStandardPrice) return
      const sell = resolveSellPrice({
        cost: this.material.cost,
        price_method: this.material.price_method,
        markup_pct: this.material.markup_pct,
        standard_margin_pct: this.standardMarginPct,
        sell_price: this.material.sell_price
      })
      this.material.sell_price = sell > 0 ? String(sell) : ''
    },
    validation (field) {
      if (field === 'description') {
        return !this.material.description || this.material.description.trim() === ''
      }
      if (field === 'cost') {
        return !this.material.cost || parseFloat(this.material.cost) <= 0
      }
      if (field === 'sell_price') {
        return !this.material.sell_price || parseFloat(this.material.sell_price) <= 0
      }
      if (field === 'type') return !this.material.type
      if (field === 'primary_supplier') return !this.material.primary_supplier
      return false
    },
    buildPayload () {
      return {
        description: this.material.description,
        cost: parseFloat(this.material.cost) || 0,
        sell_price: parseFloat(this.material.sell_price) || 0,
        price_method: this.material.price_method || PRICE_METHODS.STANDARD_MARKUP,
        markup_pct: this.material.markup_pct !== '' ? parseFloat(this.material.markup_pct) : null,
        includes_gst: this.material.includes_gst === 1 || this.material.includes_gst === true,
        is_active: this.material.is_active,
        supplier_id: this.material.supplier_id,
        size: this.material.size,
        stocked: this.material.stocked,
        brand: this.material.brand,
        type: this.material.type,
        material_type_id: this.material.material_type_id || null,
        classification_override: this.material.classification_override ? 1 : 0,
        warranty: this.material.warranty,
        code: this.material.code,
        primary_supplier: this.material.primary_supplier,
        images: Array.isArray(this.material.images) ? this.material.images : null
      }
    },
    saveProduct (close = true) {
      if (this.validation('description') || this.validation('cost') || this.validation('type') || this.validation('primary_supplier') || this.validation('sell_price')) {
        if (close !== false) {
          this.errorNotification('Please complete all required fields.')
        }
        return
      }
      const payload = this.buildPayload()
      const req = this.material.id
        ? axios.put('/api/materials/' + this.material.id, payload)
        : axios.post('/api/materials', payload)
      req.then((response) => {
        if (!this.material.id && response.data && response.data.material && response.data.material.id) {
          this.material.id = response.data.material.id
        }
        if (close) {
          this.successNotification('Product saved')
          this.$router.push('/settings/product')
        }
      }).catch((error) => {
        const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to save product'
        this.errorNotification(msg)
      })
    },
    deleteProduct () {
      if (!this.material.id) return
      const name = this.material.description || this.material.code || 'this product'
      this.swal({
        title: 'Are you sure?',
        text: 'Delete "' + name + '" from your products',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.value) {
          axios.delete('/api/materials/' + this.material.id).then(() => {
            this.successNotification('Product deleted')
            this.$router.push('/settings/product')
          }).catch((error) => {
            const msg = (error.response && error.response.data && error.response.data.error) || 'Failed to delete product'
            this.errorNotification(msg)
          })
        }
      })
    },
    resetProduct () {
      if (!this.material.id) return
      this.swal({
        title: 'Are you sure?',
        text: 'Reset this product to default values',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, reset it!'
      }).then((result) => {
        if (result.value) {
          axios.delete('/api/materials/' + this.material.id + '/reset').then(() => {
            this.successNotification('Product reset')
            this.$router.push('/settings/product')
          }).catch(() => {
            this.errorNotification('Failed to reset product')
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.page-with-subnav__content.main-container {
  margin-top: 16px;
}
.product-form-grid {
  max-width: 680px;
  margin-left: 0;
  margin-right: 0;
}
.product-form-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0;
  color: #323C47;
}
.product-form-inner {
  width: 100%;
}
@media (min-width: 768px) {
  .product-form-inner {
    max-width: none;
  }
}
.product-form-actions {
  margin-top: 1rem;
}
.product-form-actions .button + .button {
  margin-left: 0.5rem;
}
.product-amount-input[type='number']::-webkit-inner-spin-button,
.product-amount-input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.product-amount-input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
